/**
 * Content and search metadata for Product, Solutions, and Security pages.
 * Copy is drawn from the product docs (docs.mls.onchainden.com).
 */

export type PageFeature = { icon: string; title: string; body: string };

export type PageContent = {
  seoTitle: string;
  seoDescription: string;
  resources: Array<{ label: string; href: string }>;
  relatedSlugs: string[];
  eyebrow: string;
  title: string;
  titleAccent?: string;
  subtitle: string;
  features: PageFeature[];
  workflow?: {
    kind?: 'treasury' | 'security';
    title: string;
    description: string;
    steps: Array<{ title: string; description?: string }>;
  };
  faq?: {
    title: string;
    items: Array<{ q: string; a: string }>;
  };
};

export const pageContent: Record<string, PageContent> = {
  // ---- Product -----------------------------------------------------------
  'multi-layer-security': {
    seoTitle: 'Crypto Wallet Security, Multisig & Self-Custody | Den',
    seoDescription:
      'Protect digital assets with multi-party approvals and independent wallet policy checks on your device, in the Guardian, and in audited onchain contracts.',
    resources: [
      {
        label: 'How the security layers work',
        href: 'https://docs.mls.onchainden.com/enterprise-security/security-layers',
      },
      {
        label: 'Transaction approval and execution',
        href: 'https://docs.mls.onchainden.com/transaction-flow',
      },
      {
        label: 'Disaster recovery mechanisms',
        href: 'https://docs.mls.onchainden.com/enterprise-security/disaster-recovery',
      },
    ],
    relatedSlugs: [
      'policy-engine',
      'onchain-enforcement',
      'smart-contract-audits',
    ],
    eyebrow: 'Multi-layer wallet security',
    title: 'Crypto wallet security,',
    titleAccent: 'layer by layer.',
    subtitle:
      'Den is a wallet platform for organizations that need shared approvals and transaction controls. The signing client, Guardian service, and onchain smart contracts independently check the selected policy and approvals before standard execution.',
    features: [
      {
        icon: 'approvals',
        title: 'Multisig approvals, with transaction policies',
        body: 'Require multiple people to approve a payment, then limit which recipients, tokens, amounts, and contract calls the selected policy allows.',
      },
      {
        icon: 'shield',
        title: 'Three independent security checks',
        body: 'Your signing client, the Guardian, and onchain smart contracts each validate the selected policy and required approvals before standard execution.',
      },
      {
        icon: 'device',
        title: 'Your keys. Shared control.',
        body: 'Your members hold the keys that authorize transactions. The Guardian co-signs standard transactions, but its signature alone cannot authorize a transfer.',
      },
      {
        icon: 'verified',
        title: 'Security you can verify',
        body: 'Review the open-source contracts and independent Certora and yAudit reports. Verify the onchain controls protecting your digital assets.',
      },
    ],
    workflow: {
      kind: 'security',
      title: 'See how a payment passes every security layer',
      description:
        'Start by creating a transaction within policy. Requests outside the selected policy are blocked before approvals; valid payments continue through member signatures, Guardian checks, and onchain execution.',
      steps: [
        { title: 'Create a transaction within policy' },
        { title: 'Review and approve on your device' },
        { title: 'The Guardian checks the policy again' },
        { title: 'Smart contracts verify before execution' },
      ],
    },
    faq: {
      title: 'Crypto wallet security and custody FAQs',
      items: [
        {
          q: 'How does Den differ from a multisig wallet?',
          a: 'A multisig wallet requires a threshold of signatures. Den supports multi-party approvals and adds configurable rules for recipients, tokens, spending, and contract interactions. The signing client, Guardian, and smart contracts independently validate the selected policy. Smart contract multisigs such as Safe can also add controls through modules and guards.',
        },
        {
          q: 'Who controls the assets in a Den wallet?',
          a: 'Your organization’s members hold the signing keys that authorize transactions. In the standard flow, Den’s Guardian also co-signs after validating the policy and approvals. You can operate your own Guardian through a self-hosted deployment.',
        },
        {
          q: 'Can the Guardian move funds on its own?',
          a: 'A Guardian signature alone cannot authorize a standard transfer. The contracts also require member signatures and compliance with the selected transaction policy.',
        },
        {
          q: 'What happens if a server is compromised?',
          a: 'The onchain contracts still verify the selected policy and required signatures. Bypassing a server-side check does not remove those contract-level requirements. The signing client also checks the policy independently before signing.',
        },
        {
          q: 'How are changes to members and policies approved?',
          a: 'Changes to members, groups, and policies require the organization’s separate admin approval threshold. Payment approval permissions do not automatically grant permission to change those controls.',
        },
        {
          q: 'What if Den becomes permanently unavailable?',
          a: 'Den’s disaster recovery mechanism lets you recover your funds if Den becomes permanently unavailable. Recovery uses separately configured addresses and timelocks. The Transaction Recovery path bypasses the normal policy engine.',
        },
      ],
    },
  },
  'policy-engine': {
    seoTitle: 'Crypto Wallet Policy Engine & Approval Controls | Den',
    seoDescription:
      'Set crypto wallet approval thresholds, spending limits, recipient allowlists, and smart contract permissions with policies enforced across three security layers.',
    resources: [
      {
        label: 'Policy conditions and examples',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Update policies with the SDK',
        href: 'https://docs.mls.onchainden.com/api-reference/update-policies',
      },
      {
        label: 'How policies are enforced',
        href: 'https://docs.mls.onchainden.com/enterprise-security/security-layers',
      },
    ],
    relatedSlugs: ['multi-layer-security', 'treasury', 'api-sdk'],
    eyebrow: 'Crypto wallet policy engine',
    title: 'Crypto wallet policies',
    titleAccent: 'for teams and applications.',
    subtitle:
      'Den is a crypto wallet policy engine for teams and developers. Set who can initiate and approve transactions, which assets and contract actions are allowed, and how much a permitted operation can move through the dashboard or API.',
    // This page explains its controls in the capability explorer instead of cards.
    features: [],
    faq: {
      title: 'Crypto wallet policy and approval FAQs',
      items: [
        {
          q: 'What is a crypto wallet policy engine?',
          a: 'A policy engine applies the transaction rules you configure: which accounts and initiators can use a policy, what transfers or contract calls it permits, and which approvals are required. Den validates the selected policy in the signing client, Guardian service, and onchain smart contracts during standard execution.',
        },
        {
          q: 'How do policies relate to multisig approvals?',
          a: 'An approval threshold is one part of a Den policy. You can require 2 of 5 members of a group and also constrain the transaction’s asset, destination, amount, or contract call. Different policies can give different operations their own permissions and approval requirements.',
        },
        {
          q: 'Can transactions be approved automatically?',
          a: 'Yes. An automatic-approval policy can permit a defined flow without manual member approvals. The transaction must still meet that policy’s conditions and authorization requirements. Programmatic API members can initiate permitted operations through the API and TypeScript SDK.',
        },
        {
          q: 'Can I restrict tokens, recipients, and spending?',
          a: 'Token-transfer conditions can specify the token, allowed destinations, and amount threshold. An amount limit applies to a selected token. Usage limits can cap the total transferred during a configured interval, with scopes such as an initiator, source account, destination, or combined usage.',
        },
        {
          q: 'Can a policy allow only one smart contract action?',
          a: 'Yes. Contract-interaction conditions can specify the destination contract, permitted functions, and argument values. This lets you define the operation a policy authorizes rather than granting unrestricted access to every action in an application.',
        },
        {
          q: 'Who can change a policy?',
          a: 'Policy changes follow the organization’s administrative permissions and proposal workflow. Proposed changes are reviewed and collect their required approvals before taking effect. Approving a transaction under a policy does not by itself grant permission to change that policy.',
        },
      ],
    },
  },
  'dashboard-command-center': {
    seoTitle: 'Crypto Treasury Dashboard & Wallet Management | Den',
    seoDescription:
      'Manage digital asset accounts, team permissions, transaction policies, balances, and pending approvals from one enterprise crypto wallet dashboard.',
    resources: [
      {
        label: 'Accounts, members, and organizations',
        href: 'https://docs.mls.onchainden.com/core-concepts/platform',
      },
      {
        label: 'Transaction approval workflow',
        href: 'https://docs.mls.onchainden.com/transaction-flow',
      },
    ],
    relatedSlugs: ['treasury', 'policy-engine', 'mobile-signing'],
    eyebrow: 'Crypto treasury dashboard',
    title: 'Your crypto operations,',
    titleAccent: 'in one command center.',
    subtitle:
      'Manage members, policies, and assets, monitor balances in real time, and review every transaction, all from a single web dashboard.',
    features: [
      {
        icon: 'platform',
        title: 'Unified overview',
        body: 'See balances, activity, and pending approvals across every wallet and network in one place.',
      },
      {
        icon: 'roles',
        title: 'Members, groups & roles',
        body: 'Organize your team with clean separation of duties, so the right people initiate and the right people approve.',
      },
      {
        icon: 'policy',
        title: 'Manage policies visually',
        body: 'Compose and adjust the rules that govern every transaction without touching code.',
      },
      {
        icon: 'scope',
        title: 'Fully-decoded transactions',
        body: 'Review exactly what each transaction does, including amounts, recipients, and contract calls, before approval.',
      },
    ],
  },
  'api-sdk': {
    seoTitle: 'Crypto Wallet API & TypeScript SDK | Den',
    seoDescription:
      'Build with the Den crypto wallet API and TypeScript SDK. Create wallets, manage policies, and automate token transfers with local transaction validation.',
    resources: [
      {
        label: 'API and TypeScript SDK reference',
        href: 'https://docs.mls.onchainden.com/api-reference/introduction',
      },
      {
        label: 'SDK quickstart',
        href: 'https://docs.mls.onchainden.com/api-reference/quickstart',
      },
      {
        label: 'Set up an API member',
        href: 'https://docs.mls.onchainden.com/api-reference/api-member-setup',
      },
    ],
    relatedSlugs: ['wallet-as-a-service', 'policy-engine', 'payments'],
    eyebrow: 'Crypto wallet API & SDK',
    title: 'Crypto wallet API.',
    titleAccent: 'TypeScript SDK.',
    subtitle:
      'Create accounts, manage policies, and automate transactions with the Den API and TypeScript SDK. Use the SDK for local policy validation before signing, alongside Guardian and onchain checks.',
    features: [
      {
        icon: 'api',
        title: 'Programmatic transactions',
        body: 'Initiate, approve, and execute transactions in code when the selected policy permits programmatic approvals.',
      },
      {
        icon: 'waas',
        title: 'Issue wallets as a service',
        body: 'Create and manage secure, policy-driven wallets for your own customers through the API.',
      },
      {
        icon: 'policy',
        title: 'Policy-aware by default',
        body: 'API transactions use the same organization policies as the dashboard. The TypeScript SDK also validates the policy locally before signing.',
      },
      {
        icon: 'builder',
        title: 'Build once, run anywhere',
        body: 'The SDK constructs the same transactions you can build in the dashboard, ready to automate.',
      },
    ],
  },
  'no-blockchain-complexity': {
    seoTitle: 'Gas-Sponsored Crypto Wallets for Business | Den',
    seoDescription:
      'Operate enterprise crypto wallets with sponsored network fees, decoded transactions, and shared policy controls across supported EVM networks.',
    resources: [
      {
        label: 'How gas sponsorship works',
        href: 'https://docs.mls.onchainden.com/core-concepts/platform',
      },
      {
        label: 'Supported networks',
        href: 'https://docs.mls.onchainden.com/smart-contracts/deployments',
      },
    ],
    relatedSlugs: [
      'cross-chain-wallets',
      'dashboard-command-center',
      'transaction-builder',
    ],
    eyebrow: 'Gas-sponsored crypto wallets',
    title: 'Onchain operations.',
    titleAccent: 'Less complexity.',
    subtitle:
      'Den sponsors network fees and brings wallet operations into a shared dashboard and API, so your team can focus on moving assets and reviewing approvals.',
    features: [
      {
        icon: 'sparkles',
        title: 'Gas covered',
        body: 'Den sponsors network fees. Each plan includes an allowance, with usage above that allowance billed as overages.',
      },
      {
        icon: 'scope',
        title: 'Human-readable review',
        body: 'Opaque calldata and raw addresses become the action, destination, and impact that actually matter.',
      },
      {
        icon: 'crosschain',
        title: 'Shared network management',
        body: 'Manage accounts and transactions across supported EVM networks through the same dashboard and API.',
      },
      {
        icon: 'shield',
        title: 'Simple, still secure',
        body: 'Gas sponsorship works alongside the same signing-client, Guardian, and onchain transaction checks.',
      },
    ],
  },
  enterprise: {
    seoTitle: 'Enterprise Crypto Wallet Infrastructure | Den',
    seoDescription:
      'Enterprise crypto wallet infrastructure with SOC 2 Type II controls, exportable audit logs, and managed cloud, private cloud, or on-premise deployment.',
    resources: [
      {
        label: 'Enterprise security and deployment',
        href: 'https://docs.mls.onchainden.com/enterprise-security/enterprise',
      },
      {
        label: 'Recovery and self-hosted migration',
        href: 'https://docs.mls.onchainden.com/enterprise-security/disaster-recovery',
      },
      {
        label: 'Retrieve audit logs through the API',
        href: 'https://docs.mls.onchainden.com/api-reference/auditlogs/get-audit-logs',
      },
    ],
    relatedSlugs: ['treasury', 'soc2', 'multi-layer-security'],
    eyebrow: 'Enterprise crypto wallets',
    title: 'Enterprise wallet',
    titleAccent: 'infrastructure.',
    subtitle:
      'Manage digital assets with institutional governance, exportable audit trails, and deployment options that fit your security requirements.',
    features: [
      {
        icon: 'audit',
        title: 'Compliance-ready audit logs',
        body: 'Append-only logs capture every transaction, approval, policy change, and admin action, exportable for regulatory reviews.',
      },
      {
        icon: 'shield',
        title: 'SOC 2 Type II',
        body: 'Independently certified, with regular third-party security audits and 24/7 infrastructure monitoring.',
      },
      {
        icon: 'cloud',
        title: 'Deploy anywhere',
        body: 'Den Cloud (fully managed), Your Cloud (AWS, GCP, or Azure for data residency), or On-Premise for maximum control.',
      },
      {
        icon: 'support',
        title: 'Forward-deployed engineering',
        body: 'Direct access to the engineering team, dedicated account management, and prioritized incident response.',
      },
    ],
  },

  // ---- Solutions ---------------------------------------------------------
  treasury: {
    seoTitle: 'Cryptocurrency Treasury Management & Business Wallets | Den',
    seoDescription:
      'A business crypto wallet for crypto treasury management, treasury automation, and controlled payments, with shared approvals and exportable audit logs.',
    resources: [
      {
        label: 'Treasury accounts and governance',
        href: 'https://docs.mls.onchainden.com/core-concepts/platform',
      },
      {
        label: 'Approval thresholds and spending limits',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Audit trails and enterprise controls',
        href: 'https://docs.mls.onchainden.com/enterprise-security/enterprise',
      },
      {
        label: 'Recovery mechanisms and their permissions',
        href: 'https://docs.mls.onchainden.com/enterprise-security/disaster-recovery',
      },
    ],
    relatedSlugs: ['policy-engine', 'dashboard-command-center', 'payments'],
    eyebrow: 'Crypto treasury management',
    title: 'A business crypto wallet',
    titleAccent: 'for finance teams.',
    subtitle:
      'Den is a business crypto wallet for finance teams managing corporate digital assets. Organize treasury accounts, set Finance approval thresholds, and automate transfers within transaction policies through a shared dashboard and API.',
    features: [
      {
        icon: 'shield',
        title: 'Digital asset treasury management',
        body: 'Keep operating funds and reserves in separate company wallets across any EVM network.',
      },
      {
        icon: 'approvals',
        title: 'Multi-party approvals',
        body: 'Require M-of-N sign-off for high-value transfers, and auto-approve routine, low-risk flows. Govern changes to members and policies with a separate admin approval threshold.',
      },
      {
        icon: 'limits',
        title: 'Treasury automation within limits',
        body: 'Automate routine transfers through the API and SDK, with policies that cap how much can move per sender, wallet, or destination over any time window.',
      },
      {
        icon: 'audit',
        title: 'Audit-ready by default',
        body: 'Every action is logged and exportable, with a clear view of exactly what was approved and by whom.',
      },
    ],
    workflow: {
      title: 'Crypto treasury management for finance teams',
      description:
        'Follow a vendor payment from account setup and spending rules to team sign-off and transaction records.',
      steps: [
        {
          title: 'Organize accounts and team access',
          description:
            'Separate operating funds from reserves and organize members into groups such as Finance. Changes to members and account access follow the organization’s administrative approval rules.',
        },
        {
          title: 'Set payment approval rules',
          description:
            'Choose who can initiate payments, which recipients and tokens are permitted, and how many group members must approve. Configure spending and frequency limits for the relevant account, initiator, or destination.',
        },
        {
          title: 'Prepare, review, and execute payments',
          description:
            'Prepare a transfer in the dashboard or API, select the applicable policy, and collect the required member approvals. Standard execution also requires independent Guardian and onchain policy checks.',
        },
        {
          title: 'Review and export payment records',
          description:
            'Review the transaction, network, approval record, and payment reference. Retrieve transaction and administrative activity through exportable audit logs for use with your separate accounting systems.',
        },
      ],
    },
    faq: {
      title: 'Crypto treasury and business wallet FAQs',
      items: [
        {
          q: 'What is crypto treasury management?',
          a: 'Crypto treasury management covers how a team holds digital assets, controls payments, collects approvals, and maintains financial records. Den supports these day-to-day operations with organization accounts, transaction policies, and audit logs.',
        },
        {
          q: 'What should a corporate crypto wallet provide?',
          a: 'A crypto wallet for business needs shared controls for company funds: defined payment permissions, approval thresholds, spending limits, and a record of who did what. Den brings these controls into the dashboard and API so finance teams can manage company assets together.',
        },
        {
          q: 'How does treasury automation work?',
          a: 'Use the API or SDK to prepare routine transfers, with policies for recipients, tokens, payment amounts, and spending over time. Policies can auto-approve eligible transfers or require named members or groups to approve them. Payments execute only when the required approvals are present.',
        },
        {
          q: 'Does Den replace accounting or payroll software?',
          a: 'No, Den manages crypto wallets, including controls, payments, and other onchain transactions. You can send payroll payments with Den. However, you will need to integrate external accounting and payroll software for bookkeeping, wage calculations, deductions, and related tasks.',
        },
        {
          q: 'What happens if a signer is unavailable?',
          a: 'A policy requiring two of three Finance members can still be approved when one member is away. If the remaining members cannot meet the threshold, the payment cannot proceed under that policy. Changes to members, groups, or policies require the organization’s admin approval threshold.',
        },
        {
          q: 'What if Den becomes permanently unavailable?',
          a: 'Den’s disaster recovery mechanism lets you recover your funds if Den becomes permanently unavailable.',
        },
      ],
    },
  },
  payments: {
    seoTitle: 'Stablecoin Payments & Automated Crypto Payouts | Den',
    seoDescription:
      'Automate stablecoin payments and crypto payouts with the Den API and SDK. Set recipient allowlists, spending limits, and multi-party approval requirements.',
    resources: [
      {
        label: 'Send USDC and other ERC-20 tokens',
        href: 'https://docs.mls.onchainden.com/api-reference/send-tokens',
      },
      {
        label: 'Configure payment policies',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Automate with the API and SDK',
        href: 'https://docs.mls.onchainden.com/core-concepts/api-sdk',
      },
    ],
    relatedSlugs: ['policy-engine', 'api-sdk', 'treasury'],
    eyebrow: 'Stablecoin payments & payouts',
    title: 'Stablecoin payments,',
    titleAccent: 'under your control.',
    subtitle:
      'Send stablecoins such as USDC through the dashboard or TypeScript SDK. Govern payouts with approved recipients, token and amount conditions, and the right approval threshold for each flow.',
    features: [
      {
        icon: 'api',
        title: 'Programmatic payouts',
        body: 'Create token transfers and submit API-member signatures through the SDK. Execute once the policy’s required approvals are present.',
      },
      {
        icon: 'scope',
        title: 'Recipient allowlists',
        body: 'Restrict payouts to approved addresses, specific tokens, and amount thresholds.',
      },
      {
        icon: 'approvals',
        title: 'Right-sized oversight',
        body: 'Auto-approve small, routine payments and route larger ones to the right approvers.',
      },
      {
        icon: 'shield',
        title: 'Enforced on every layer',
        body: 'The same policies are checked on the device, in the cloud, and on-chain.',
      },
    ],
  },
  'onchain-operations': {
    seoTitle: 'Onchain Operations & DeFi Transaction Controls | Den',
    seoDescription:
      'Govern DeFi and smart contract operations with contract allowlists, function permissions, argument filters, and transaction approval policies.',
    resources: [
      {
        label: 'Smart contract policy conditions',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Deposit into DeFi with the SDK',
        href: 'https://docs.mls.onchainden.com/api-reference/deposit-into-defi',
      },
      {
        label: 'Transaction execution flow',
        href: 'https://docs.mls.onchainden.com/transaction-flow',
      },
    ],
    relatedSlugs: ['transaction-builder', 'policy-engine', 'api-sdk'],
    eyebrow: 'Onchain operations & DeFi',
    title: 'Run onchain strategies',
    titleAccent: 'with clear controls.',
    subtitle:
      'Govern smart contract interactions and DeFi activity with policies for contract addresses, functions, and argument values. Review calls and collect approvals before execution.',
    features: [
      {
        icon: 'scope',
        title: 'Contract-level controls',
        body: 'Allow specific contracts, functions, and argument values, and nothing else.',
      },
      {
        icon: 'defi',
        title: 'DeFi, governed',
        body: 'Interact with protocols while keeping approvals, limits, and allowlists in force.',
      },
      {
        icon: 'box',
        title: 'Decoded before you sign',
        body: 'Review exactly what each transaction does before it is approved.',
      },
      {
        icon: 'shield',
        title: 'Onchain-enforced',
        body: 'Smart contracts independently verify the selected policy and required approvals before executing standard transactions.',
      },
    ],
  },
  'wallet-as-a-service': {
    seoTitle: 'Wallet-as-a-Service (WaaS) Infrastructure | Den',
    seoDescription:
      'Issue customer wallets with Den Wallet-as-a-Service. Use the API and TypeScript SDK to create accounts, manage policies, and automate transactions.',
    resources: [
      {
        label: 'Wallet-as-a-Service and API capabilities',
        href: 'https://docs.mls.onchainden.com/core-concepts/api-sdk',
      },
      {
        label: 'Create wallet accounts with the SDK',
        href: 'https://docs.mls.onchainden.com/api-reference/create-account',
      },
      {
        label: 'Register a programmatic API member',
        href: 'https://docs.mls.onchainden.com/api-reference/api-member-setup',
      },
    ],
    relatedSlugs: ['api-sdk', 'policy-engine', 'enterprise'],
    eyebrow: 'Wallet-as-a-Service (WaaS)',
    title: 'Wallet-as-a-Service,',
    titleAccent: 'governed by your rules.',
    subtitle:
      'Build customer wallets into your application with the Den API and TypeScript SDK. Create accounts, assign transaction policies, and automate operations with programmatic API members.',
    features: [
      {
        icon: 'api',
        title: 'Programmable issuance',
        body: 'Create and manage wallets for your customers entirely through the API and SDK.',
      },
      {
        icon: 'shield',
        title: 'Multi-layer wallet controls',
        body: 'Customer wallets use the same client, Guardian, and onchain policy validation as internal operations.',
      },
      {
        icon: 'policy',
        title: 'Your rules, their wallets',
        body: 'Define the policies that govern how funds can move for each customer.',
      },
      {
        icon: 'enterprise',
        title: 'Deploy your way',
        body: 'Run on Den Cloud, your own cloud, or on-premise to meet your compliance needs.',
      },
    ],
  },
  tokenization: {
    seoTitle: 'Tokenization Operations for Stablecoins & RWAs | Den',
    seoDescription:
      'Govern stablecoin and real-world asset token operations with smart contract permissions, approval thresholds, and audit trails for your organization.',
    resources: [
      {
        label: 'Contract permissions and RWA policy examples',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Programmatic smart contract operations',
        href: 'https://docs.mls.onchainden.com/core-concepts/api-sdk',
      },
      {
        label: 'Enterprise audit trails',
        href: 'https://docs.mls.onchainden.com/enterprise-security/enterprise',
      },
    ],
    relatedSlugs: ['onchain-operations', 'policy-engine', 'treasury'],
    eyebrow: 'Stablecoin & RWA operations',
    title: 'Tokenization operations.',
    titleAccent: 'Clear governance.',
    subtitle:
      'Control the wallet transactions behind stablecoin and real-world asset operations. Scope contract permissions and require the right team approvals for sensitive token actions.',
    features: [
      {
        icon: 'coins',
        title: 'Programmatic issuance',
        body: 'Submit calls to your token contracts through the dashboard or API, with the selected transaction policy applied to each action.',
      },
      {
        icon: 'policy',
        title: 'Controls built into the workflow',
        body: 'Require approval from designated members or groups for token contract actions, including RWA allowlist changes.',
      },
      {
        icon: 'approvals',
        title: 'Governed by policy',
        body: 'Restrict calls by contract address, function, and argument values, with M-of-N approvals for sensitive actions.',
      },
      {
        icon: 'audit',
        title: 'Operational visibility',
        body: 'Review transaction activity, approvals, policy changes, and administrative actions through exportable audit logs.',
      },
    ],
  },

  // ---- Product (additional) ---------------------------------------------
  'mobile-signing': {
    seoTitle: 'Mobile Crypto Transaction Signing & Approvals | Den',
    seoDescription:
      'Review and sign crypto transactions with the Den mobile signing client. Local policy validation checks transaction rules before a signature is produced.',
    resources: [
      {
        label: 'Signing client security layer',
        href: 'https://docs.mls.onchainden.com/enterprise-security/security-layers',
      },
      {
        label: 'Mobile approval workflow',
        href: 'https://docs.mls.onchainden.com/transaction-flow',
      },
    ],
    relatedSlugs: [
      'multi-layer-security',
      'dashboard-command-center',
      'policy-engine',
    ],
    eyebrow: 'Mobile transaction signing',
    title: 'Mobile signing.',
    titleAccent: 'Local policy checks.',
    subtitle:
      'A dedicated mobile signing client validates and approves transactions, with every signature produced on-device.',
    features: [
      {
        icon: 'device',
        title: 'On-device validation',
        body: 'The policy engine refuses to produce a signature for anything that breaks your rules.',
      },
      {
        icon: 'approvals',
        title: 'Approve on the go',
        body: 'Review and approve or reject pending transactions from your phone, wherever you are.',
      },
      {
        icon: 'shield',
        title: 'Keys never leave the device',
        body: 'Signing happens locally, and key material is never exposed to a server.',
      },
      {
        icon: 'scope',
        title: 'Clear, decoded prompts',
        body: 'See exactly what you are approving, including amounts, recipients, and contract calls, before you sign.',
      },
    ],
  },
  'transaction-builder': {
    seoTitle: 'Crypto Transaction Builder & Smart Contract Calls | Den',
    seoDescription:
      'Build token transfers, smart contract calls, and DeFi transactions in the Den dashboard, with decoded inputs and policy checks before approval.',
    resources: [
      {
        label: 'Transaction types and policy conditions',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Transaction approval and execution',
        href: 'https://docs.mls.onchainden.com/transaction-flow',
      },
      {
        label: 'Construct token transfers with the SDK',
        href: 'https://docs.mls.onchainden.com/api-reference/send-tokens',
      },
    ],
    relatedSlugs: ['onchain-operations', 'mobile-signing', 'api-sdk'],
    eyebrow: 'Crypto transaction builder',
    title: 'Build onchain transactions,',
    titleAccent: 'with clear review.',
    subtitle:
      'Construct token transfers, contract calls, and DeFi interactions in the dashboard, fully decoded before they are signed.',
    features: [
      {
        icon: 'builder',
        title: 'Compose any call',
        body: 'Build transfers and arbitrary contract interactions with guided, typed parameter inputs.',
      },
      {
        icon: 'scope',
        title: 'Full decoding',
        body: 'See the exact function, arguments, and effects of every transaction before approval.',
      },
      {
        icon: 'policy',
        title: 'Policy-checked as you build',
        body: 'The builder reflects what your policies will and will not allow, up front.',
      },
      {
        icon: 'api',
        title: 'Or build it in code',
        body: 'Construct and submit the same transactions programmatically through the SDK.',
      },
    ],
  },
  'cross-chain-wallets': {
    seoTitle: 'Multi-Chain EVM Wallets for Enterprise | Den',
    seoDescription:
      'Manage enterprise wallets across supported EVM networks with shared governance, transaction approvals, and a unified view of accounts and balances.',
    resources: [
      {
        label: 'Supported networks and contract addresses',
        href: 'https://docs.mls.onchainden.com/smart-contracts/deployments',
      },
      {
        label: 'Create accounts across networks',
        href: 'https://docs.mls.onchainden.com/api-reference/create-account',
      },
      {
        label: 'Query multi-network account balances',
        href: 'https://docs.mls.onchainden.com/api-reference/accounts/get-account-balances',
      },
    ],
    relatedSlugs: ['treasury', 'no-blockchain-complexity', 'policy-engine'],
    eyebrow: 'Multi-chain EVM wallets',
    title: 'Multi-chain wallets.',
    titleAccent: 'One policy model.',
    subtitle:
      'Hold and govern digital assets across supported EVM networks from a single organization, with shared policies and account visibility.',
    features: [
      {
        icon: 'crosschain',
        title: 'Multi-network accounts',
        body: 'Operate accounts across the EVM networks your business actually uses.',
      },
      {
        icon: 'policy',
        title: 'One policy model everywhere',
        body: 'The same governance and approvals apply consistently across every chain.',
      },
      {
        icon: 'box',
        title: 'Per-network deployments',
        body: 'Contracts are deployed and documented per supported network.',
      },
      {
        icon: 'platform',
        title: 'Unified view',
        body: 'Monitor balances and activity across all your chains in one place.',
      },
    ],
  },

  // ---- Security ----------------------------------------------------------
  'cloud-compromise-immunity': {
    seoTitle: 'Crypto Wallet Security Beyond Cloud Infrastructure | Den',
    seoDescription:
      'Reduce reliance on cloud security with independent signing-client, Guardian, and onchain policy checks for enterprise crypto wallet transactions.',
    resources: [
      {
        label: 'Independent security layers',
        href: 'https://docs.mls.onchainden.com/enterprise-security/security-layers',
      },
      {
        label: 'Onchain contract enforcement',
        href: 'https://docs.mls.onchainden.com/smart-contracts/overview',
      },
      {
        label: 'Recovery controls and timelocks',
        href: 'https://docs.mls.onchainden.com/enterprise-security/disaster-recovery',
      },
    ],
    relatedSlugs: [
      'multi-layer-security',
      'onchain-enforcement',
      'open-source',
    ],
    eyebrow: 'Cloud compromise protection',
    title: 'Wallet security',
    titleAccent: 'beyond the cloud.',
    subtitle:
      'Onchain policy enforcement adds an independent control beyond your servers. A compromised cloud does not remove the contract-level approval and transaction requirements in the standard execution flow.',
    features: [
      {
        icon: 'cloudoff',
        title: 'Contract-level checks',
        body: 'Standard execution requires the smart contracts to verify policies and approvals independently of the server.',
      },
      {
        icon: 'lock',
        title: 'Independent layers',
        body: 'The signing client, Guardian, and smart contracts each run their own policy validation.',
      },
      {
        icon: 'server',
        title: 'Breach-tolerant by design',
        body: 'Contract-level transaction and approval checks remain a separate control from the offchain infrastructure.',
      },
      {
        icon: 'verified',
        title: 'Verifiable controls',
        body: 'Review the open-source contracts, published audits, and recovery configuration as part of your security assessment.',
      },
    ],
  },
  'onchain-enforcement': {
    seoTitle: 'Onchain Policy Enforcement for Crypto Wallets | Den',
    seoDescription:
      'Enforce crypto wallet transaction policies in audited smart contracts. Verify approvals, spending rules, and permissions onchain before execution.',
    resources: [
      {
        label: 'Smart contract architecture',
        href: 'https://docs.mls.onchainden.com/smart-contracts/overview',
      },
      {
        label: 'Policy configuration',
        href: 'https://docs.mls.onchainden.com/core-concepts/policies',
      },
      {
        label: 'Recovery paths and policy exceptions',
        href: 'https://docs.mls.onchainden.com/enterprise-security/disaster-recovery',
      },
    ],
    relatedSlugs: ['policy-engine', 'smart-contract-audits', 'open-source'],
    eyebrow: 'Onchain policy enforcement',
    title: 'Wallet policies,',
    titleAccent: 'enforced onchain.',
    subtitle:
      'Den provides onchain wallet policy enforcement for organizations. Audited smart contracts independently check the selected transaction policy and required approvals before standard execution, beyond the dashboard and servers.',
    features: [
      {
        icon: 'lock',
        title: 'Re-validated on-chain',
        body: 'In the standard execution flow, smart contracts verify the selected policy and revert transactions that fail its requirements.',
      },
      {
        icon: 'box',
        title: 'Merkle-backed policies',
        body: 'Policies are stored and verified efficiently with cryptographic proofs.',
      },
      {
        icon: 'shield',
        title: 'EIP-712 signing',
        body: 'Typed, nonce-managed signatures with a clear, verifiable message format.',
      },
      {
        icon: 'support',
        title: 'Disaster recovery',
        body: 'Timelocked recovery can replace the Guardian or execute transactions directly. Transaction Recovery bypasses policy checks and uses a dedicated recovery address.',
      },
    ],
  },
  'smart-contract-audits': {
    seoTitle: 'Smart Contract Security Audits by Certora & yAudit | Den',
    seoDescription:
      'Review Den smart contract security audits by Certora and yAudit, inspect the open-source contracts, and verify documented onchain deployments.',
    resources: [
      {
        label: 'Published Certora and yAudit reports',
        href: 'https://docs.mls.onchainden.com/smart-contracts/audits',
      },
      {
        label: 'Open-source smart contracts',
        href: 'https://docs.mls.onchainden.com/smart-contracts/overview',
      },
      {
        label: 'Verify deployed contract addresses',
        href: 'https://docs.mls.onchainden.com/smart-contracts/deployments',
      },
    ],
    relatedSlugs: [
      'open-source',
      'onchain-enforcement',
      'multi-layer-security',
    ],
    eyebrow: 'Smart contract security audits',
    title: 'Smart contract audits',
    titleAccent: 'you can review.',
    subtitle:
      'Review independent security assessments from Certora and yAudit, then inspect the open-source smart contracts and documented deployments.',
    features: [
      {
        icon: 'verified',
        title: 'Audited by Certora',
        body: 'Read Certora’s published security assessment of the core smart contracts.',
      },
      {
        icon: 'verified',
        title: 'Audited by yAudit',
        body: 'An independent third-party security review of the system.',
      },
      {
        icon: 'opensource',
        title: 'Open-source',
        body: 'The smart contract codebase is public, so you can verify deployed contracts against the published source.',
      },
      {
        icon: 'box',
        title: 'Hardened architecture',
        body: 'Merkle-tree policy storage, EIP-712 signing, and a dedicated executor module.',
      },
    ],
  },
  soc2: {
    seoTitle: 'SOC 2 Type II & Enterprise Wallet Audit Trails | Den',
    seoDescription:
      'Explore Den SOC 2 Type II controls, append-only audit logs, independent security reviews, and continuous monitoring for enterprise wallet infrastructure.',
    resources: [
      {
        label: 'Enterprise security and SOC 2',
        href: 'https://docs.mls.onchainden.com/enterprise-security/enterprise',
      },
      {
        label: 'Export audit logs through the API',
        href: 'https://docs.mls.onchainden.com/api-reference/auditlogs/get-audit-logs',
      },
    ],
    relatedSlugs: ['enterprise', 'smart-contract-audits', 'treasury'],
    eyebrow: 'Enterprise security & auditability',
    title: 'SOC 2 Type II.',
    titleAccent: 'Operational oversight.',
    subtitle:
      'Den maintains SOC 2 Type II certification, with independent security audits, exportable activity logs, and continuous infrastructure monitoring.',
    features: [
      {
        icon: 'cert',
        title: 'SOC 2 Type II',
        body: 'Independently certified and continuously maintained.',
      },
      {
        icon: 'audit',
        title: 'Append-only audit logs',
        body: 'Every transaction, approval, policy change, and admin action is captured and exportable.',
      },
      {
        icon: 'support',
        title: '24/7 monitoring',
        body: 'Continuous infrastructure monitoring with incident response.',
      },
      {
        icon: 'shield',
        title: 'Regular security audits',
        body: 'Ongoing independent reviews keep the security bar high.',
      },
    ],
  },
  'open-source': {
    seoTitle: 'Open-Source Crypto Wallet Smart Contracts | Den',
    seoDescription:
      'Inspect Den open-source wallet smart contracts, review security audits, and verify deployed contract addresses across supported EVM networks.',
    resources: [
      {
        label: 'Smart contract source and architecture',
        href: 'https://docs.mls.onchainden.com/smart-contracts/overview',
      },
      {
        label: 'Independent security audit reports',
        href: 'https://docs.mls.onchainden.com/smart-contracts/audits',
      },
      {
        label: 'Networks and deployed addresses',
        href: 'https://docs.mls.onchainden.com/smart-contracts/deployments',
      },
    ],
    relatedSlugs: [
      'smart-contract-audits',
      'onchain-enforcement',
      'cross-chain-wallets',
    ],
    eyebrow: 'Open-source wallet contracts',
    title: 'Open-source contracts.',
    titleAccent: 'Verifiable security.',
    subtitle:
      'Read the smart contract source, verify deployments, and review the onchain security properties independently.',
    features: [
      {
        icon: 'opensource',
        title: 'Public on GitHub',
        body: 'The entire smart-contract codebase is open for review.',
      },
      {
        icon: 'verified',
        title: 'Verify against source',
        body: 'Confirm the deployed contracts match the published code.',
      },
      {
        icon: 'box',
        title: 'Documented deployments',
        body: 'Supported networks and contract addresses are documented.',
      },
      {
        icon: 'lock',
        title: 'Security by transparency',
        body: 'Confidence comes from verifiability, not obscurity.',
      },
    ],
  },
};
